let player = null;

async function getFreshSpotifyToken() {
  const res = await fetch("/api/spotify/token");
  const data = await res.json().catch(() => ({}));

  if (!res.ok || !data.access_token) {
    throw new Error(data?.error || "Spotify token unavailable");
  }

  return data.access_token;
}

function loadSpotifySDK() {
  return new Promise((resolve) => {
    if (window.Spotify) return resolve();

    if (document.getElementById("spotify-player-sdk")) {
      const wait = setInterval(() => {
        if (window.Spotify) {
          clearInterval(wait);
          resolve();
        }
      }, 100);
      return;
    }

    window.onSpotifyWebPlaybackSDKReady = () => resolve();

    const script = document.createElement("script");
    script.id = "spotify-player-sdk";
    script.src = "https://sdk.scdn.co/spotify-player.js";
    script.async = true;
    document.body.appendChild(script);
  });
}

export async function initSpotifyPlayer(onReady, onStateChange) {
  if (player) return player;

  await loadSpotifySDK();

  player = new window.Spotify.Player({
    name: "Poster Junkie Player",
    getOAuthToken: async (cb) => cb(await getFreshSpotifyToken()),
    volume: 0.7,
  });

  player.addListener("ready", ({ device_id }) => {
    console.log("Spotify Player Ready:", device_id);
    onReady?.(device_id);
  });

  player.addListener("player_state_changed", (state) => {
    onStateChange?.(state);
  });

  player.addListener("initialization_error", console.error);
  player.addListener("authentication_error", console.error);
  player.addListener("account_error", console.error);
  player.addListener("playback_error", console.error);

  await player.connect();
  return player;
}

async function assertSpotifyResponse(res, label) {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.error(`${label} failed:`, res.status, text);
    throw new Error(`${label} failed: ${res.status} ${text}`);
  }
}

export async function transferPlayback(deviceId) {
  const token = await getFreshSpotifyToken();

  const res = await fetch("https://api.spotify.com/v1/me/player", {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      device_ids: [deviceId],
      play: false,
    }),
  });

  await assertSpotifyResponse(res, "Spotify transferPlayback");
}

export async function playUri(uri, deviceId) {
  const token = await getFreshSpotifyToken();

  console.log("Spotify playUri:", { uri, deviceId });

  await transferPlayback(deviceId);

  const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uris: [uri],
    }),
  });

  await assertSpotifyResponse(res, "Spotify playUri");
}

export async function pausePlayback() {
  const token = await getFreshSpotifyToken();

  const res = await fetch("https://api.spotify.com/v1/me/player/pause", {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
  });

  await assertSpotifyResponse(res, "Spotify pausePlayback");
}

export async function resumePlayback(deviceId) {
  const token = await getFreshSpotifyToken();

  const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
  });

  await assertSpotifyResponse(res, "Spotify resumePlayback");
}

export async function nextTrack() {
  const token = await getFreshSpotifyToken();

  const res = await fetch("https://api.spotify.com/v1/me/player/next", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  await assertSpotifyResponse(res, "Spotify nextTrack");
}

export async function previousTrack() {
  const token = await getFreshSpotifyToken();

  const res = await fetch("https://api.spotify.com/v1/me/player/previous", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  await assertSpotifyResponse(res, "Spotify previousTrack");
}

export async function playContext(contextUri, trackUri, deviceId) {
  const token = await getFreshSpotifyToken();

  console.log("Spotify playContext:", { contextUri, trackUri, deviceId });

  await transferPlayback(deviceId);

  const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      context_uri: contextUri,
      offset: { uri: trackUri },
    }),
  });

  await assertSpotifyResponse(res, "Spotify playContext");
}


export async function activateSpotifyPlayer() {
  if (!player) return;

  try {
    await player.activateElement();
    console.log("Spotify player activated by user gesture.");
  } catch (err) {
    console.warn("Spotify activateElement failed:", err);
  }
}


export async function setPlayerVolume(volume) {
  if (!player) return;

  try {
    await player.setVolume(volume);
    console.log("Spotify volume set:", volume);
  } catch (err) {
    console.warn("Spotify setVolume failed:", err);
  }
}

export async function seekToPosition(ms) {
  if (!player) return;

  try {
    await player.seek(ms);
    console.log("Spotify seek:", ms);
  } catch (err) {
    console.warn("Spotify seek failed:", err);
  }
}

export async function resumeCurrentPlayback() {
  const token = await getFreshSpotifyToken();

  const res = await fetch("https://api.spotify.com/v1/me/player/play", {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
  });

  await assertSpotifyResponse(res, "Spotify resumeCurrentPlayback");
}

export async function seekSpotifyPlayback(ms) {
  const token = await getFreshSpotifyToken();

  const res = await fetch(`https://api.spotify.com/v1/me/player/seek?position_ms=${Math.max(0, Math.floor(ms))}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
  });

  await assertSpotifyResponse(res, "Spotify seekSpotifyPlayback");
}

export async function playContextOnly(contextUri, deviceId) {
  const token = await getFreshSpotifyToken();

  await transferPlayback(deviceId);

  const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      context_uri: contextUri,
    }),
  });

  await assertSpotifyResponse(res, "Spotify playContextOnly");
}
